/**
 * Pure functions for calculating permissions based on user and item organizations
 * These functions can be tested without React hooks
 *
 * IMPORTANT: Items are always in the Organization default group, even when assigned
 * to a sub-team. The user's effective permission is the HIGHEST role across ALL
 * matching groups. This means:
 * - If user is Viewer in a sub-team but Editor in the Org group → Editor wins
 * - To have read-only access, user must be Viewer (or lower) in ALL matching groups
 */

/**
 * Calculate if user has write access to an item based on org/group membership
 *
 * @param {Object} user - User object with organizations array
 * @param {Object} item - Item object with organizations array
 * @param {boolean} isInternal - Whether user is internal (Feels Music employee)
 * @returns {boolean} - True if user has write access (NOT read-only)
 */
export function calculateWriteAccess(user, item, isInternal) {
  // Internal users (Feels Music employees) can edit everything
  if (isInternal) {
    return true
  }

  // No item or no user = no write access
  if (!item || !user) {
    return false
  }

  // If item has no organizations, no write access
  if (!item.organizations || item.organizations.length === 0) {
    return false
  }

  // Check if user has write access in ANY of the item's org/group combinations
  for (const itemOrg of item.organizations) {
    // Find this org in user's account data
    const userOrg = (user.organizations || []).find(
      org => org.f_id === itemOrg.organization_f_id
    )

    if (!userOrg) {
      continue // User not in this org
    }

    // Loop through item's groups within this org
    for (const itemGroupId of itemOrg.organization_groups || []) {
      // Find this group in user's org data
      const userGroup = (
        userOrg.organization_user?.organization_groups || []
      ).find(g => g.f_id === itemGroupId)

      if (!userGroup) {
        continue // User not in this group
      }

      // Check if user has write access in this group
      const hasWrite = hasWritePermissionInGroup(userGroup)

      if (hasWrite) {
        return true // Found write access!
      }
    }
  }

  return false // No write access found
}

/**
 * Check if user has write permission in a group
 * Write access means: Editor role or higher (Owner, Admin, Editor)
 *
 * Note: We use role-based checks only, not individual permission strings.
 * Per-resource permission refinement (e.g., music_artist:r vs music_song:w)
 * can be added later if granular content-type restrictions are needed.
 *
 * @param {Object} userGroup - Group object with roles and permissions
 * @returns {boolean} - True if user has write access in this group
 */
export function hasWritePermissionInGroup(userGroup) {
  const roles = userGroup.roles || []
  for (const roleObj of roles) {
    if (isWriteRole(roleObj.role)) {
      return true
    }
  }
  return false
}

/**
 * Check if role grants write access
 *
 * @param {string} roleName - Name of the role
 * @returns {boolean} - True if role grants write access
 */
export function isWriteRole(roleName) {
  const writeRoles = [
    'Editor',
    'Organization Editor',
    'Admin',
    'Organization Administrator',
    'Organization Admin',
    'Group Administrator',
    'Team Administrator',
    'Owner',
    'Organization Owner',
  ]
  return writeRoles.includes(roleName)
}

/**
 * Check if permission string grants write access
 * Examples: "music_song:*", "music_song:rwad", "music_song:w"
 * Read-only example: "music_song:r"
 *
 * @param {string} permString - Permission string (e.g., "music_artist:*")
 * @returns {boolean} - True if permission grants write access
 */
export function hasWriteInPermission(permString) {
  // Extract the access flags after the colon
  const parts = permString.split(':')
  if (parts.length !== 2) return false

  const accessFlags = parts[1]

  // Check for write indicators: *, w, a, d (anything other than just "r")
  if (accessFlags === 'r') {
    return false // Read-only
  }

  // If it has *, w, a, d, or any combination, it's write access
  return accessFlags.length > 0
}
